import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { title: "", date: "", end_date: "", description: "", region: "" };

export default function HolidayManagement() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/holidays"); setItems(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, end_date: form.end_date || null };
      if (editingId) await api.put(`/holidays/${editingId}`, payload);
      else await api.post("/holidays", payload);
      toast.success(editingId ? "Updated" : "Added"); setShowForm(false); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const edit = (h) => { setEditingId(h.id); setForm({ ...h, end_date: h.end_date || "" }); setShowForm(true); };
  const add = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.title}"?`)) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-1">Calendar</div>
          <h1 className="text-3xl font-black tracking-tight">Holiday Management</h1>
        </div>
        {isAdmin && <button onClick={add} className="tl-btn flex items-center gap-2" data-testid="add-holiday-btn"><Plus className="w-4 h-4" /> Add Holiday</button>}
      </div>

      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA] text-xs uppercase tracking-[0.1em] text-gray-600">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Region</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Description</th>
              {isAdmin && <th className="px-4 py-3 w-24"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid="holidays-table">
            {items.map((h) => (
              <tr key={h.id} className="hover:bg-[#F8F9FA]">
                <td className="px-4 py-3 font-medium">{h.date}{h.end_date ? ` – ${h.end_date}` : ""}</td>
                <td className="px-4 py-3">{h.title}</td>
                <td className="px-4 py-3 hidden md:table-cell text-gray-500">{h.region}</td>
                <td className="px-4 py-3 hidden md:table-cell text-gray-500 truncate max-w-xs">{h.description}</td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => edit(h)} className="p-1.5 rounded hover:bg-gray-100"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(h)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={isAdmin ? 5 : 4} className="px-4 py-10 text-center text-gray-500">No holidays yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="holiday-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingId ? "Edit" : "Add"} Holiday</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <InputF label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            <div className="grid grid-cols-2 gap-3">
              <InputF label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
              <InputF label="End Date (optional)" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
            </div>
            <InputF label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="3" className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]" />
            </div>
            <button type="submit" className="tl-btn w-full" data-testid="holiday-form-submit">{editingId ? "Update" : "Create"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

function InputF({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]" />
    </div>
  );
}
